import { Container } from '@mui/material';
import { useParams } from 'react-router-dom';
import { generateRoutePath, MyParams, RouteEnum, useGotoRoute } from 'App';
import {
  BreadCrumbs,
  EnhancedTable,
  HeadCell,
  Loader,
  useCourse,
  useExam,
  usePagination,
  usePartner,
  useSorting,
} from '@shared';
import { ExamSessionTableCells } from './ExamSessionTableCells';
import { ColumnsIdsAPI } from './ExamSessions.types';
import SectionHeader from './components/SectionHeader';
import { useScrollToTopVisibility } from '../../shared/hooks/useScrollToTopVisibility';
import { ScrollToTopButton } from '../../shared/components/ScrollToTopButton';
import { useExamSessionsFilters } from './hooks/useExamSessionsFilters';
import { useEffect, useState } from 'react';

const headCells: readonly HeadCell<ColumnsIdsAPI>[] = [
  {
    id: 'first_name',
    label: 'First Name',
    sortable: true,
    sx: { px: 1.5 },
  },
  {
    id: 'last_name',
    label: 'Last Name',
    sortable: true,
    sx: { px: 1.5 },
  },
  {
    id: 'email',
    label: 'Email',
    sortable: false,
    sx: { width: 120, maxWidth: 120, minWidth: 120, px: 1.5 },
  },
  {
    id: 'start_time',
    label: 'Exam Start Time',
    sortable: true,
    sx: { px: 1.5 },
  },
  {
    id: 'end_time',
    label: 'Exam End Time',
    sortable: true,
    sx: { px: 1.5 },
  },
  {
    id: 'duration',
    label: 'Duration',
    sortable: true,
    sx: { px: 1.5 },
  },
  {
    id: 'anomaly_count',
    label: 'Anomalies',
    sortable: true,
    sx: { width: 118, maxWidth: 118, minWidth: 118, px: 1.5 },
  },
  {
    id: 'audit_status',
    label: 'Audit Status',
    sortable: true,
    sx: { px: 1.5 },
  },
  {
    id: 'actions',
    label: '',
    sortable: false,
    sx: {
      width: 56,
      maxWidth: 56,
      minWidth: 56,
      px: 0.5,
    },
  },
];

export function ExamSessions() {
  const { partner, course, exam } = useParams<keyof MyParams>() as MyParams;
  const [page, setPage] = usePagination();
  const [{ order, orderBy, rowsPerPage, auditOfInterest }, dispatch] = useSorting(headCells);
  const [isTableDataLoading, setIsTableDataLoading] = useState(false);
  const { name: partnerName } = usePartner({ partner });
  const courseName = useCourse({ partner, course });
  const examName = useExam({ exam });
  const showScrollTop = useScrollToTopVisibility();

  const { data, isLoading, isError, filters } = useExamSessionsFilters({
    exam,
    partner,
    course,
    orderBy,
    order,
    rowsPerPage,
    auditOfInterest,
    pageNavigationTool: { page, setPage },
  });

  const anomalySelected = filters[0]?.selectedOptions;
  const auditStatusSelected = filters[1]?.selectedOptions;

  useEffect(() => {
    setIsTableDataLoading(true);
  }, [anomalySelected, auditStatusSelected]);

  useEffect(() => {
    if (data) {
      setIsTableDataLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (!isTableDataLoading) return;

    const failSafe = setTimeout(() => {
      console.warn('Forcefully cleared isLoading (fail-safe triggered)');
      setIsTableDataLoading(false);
    }, 1000);

    return () => clearTimeout(failSafe);
  }, [isTableDataLoading, setIsTableDataLoading]);

  const goToRoute = useGotoRoute(RouteEnum.ExamAudit);

  const breadcrumbsNameMap = [
    {
      path: generateRoutePath({ name: RouteEnum.Courses, params: { partner } }),
      name: partnerName,
    },
    {
      path: generateRoutePath({ name: RouteEnum.Exams, params: { partner, course } }),
      name: courseName,
    },
    {
      path: generateRoutePath({ name: RouteEnum.ExamSessions, params: { partner, course, exam } }),
      name: examName,
    },
  ];

  if (isLoading || !data) {
    return <Loader />;
  }
  if (isError) {
    return null;
  }
  return (
    <Container component='article'>
      <BreadCrumbs breadcrumbNameMap={breadcrumbsNameMap} />
      <SectionHeader examName={examName} />
      <EnhancedTable
        columns={headCells}
        rows={data.sessions}
        totalRows={data?.pagination.totalCount}
        onRowClick={goToRoute}
        entityName='session'
        renderElems={({
          uuid,
          auditUuid,
          userFirstName,
          userLastName,
          userEmail,
          examStartTime,
          examEndTime,
          examDuration,
          anomalyCount,
          shortExam,
          recordOnlyExam,
          clickable,
          auditOfInterest,
          auditStatus,
          failed,
        }) => (
          <ExamSessionTableCells
            entries={Object.entries({
              userFirstName,
              userLastName,
              userEmail,
              examStartTime,
              examEndTime,
              examDuration,
              anomalyCount,
              auditStatus,
            })}
            isAuditOfInterest={auditOfInterest}
            isShort={shortExam}
            isRecordOnly={recordOnlyExam}
            isFailed={failed}
            auditStatus={auditStatus}
            audiUuid={auditUuid}
            key={uuid + clickable}
          />
        )}
        sortingState={{ order, orderBy, rowsPerPage, auditOfInterest }}
        totalPages={data.pagination.totalPages}
        onPageChange={setPage}
        currentPage={page}
        dispatch={dispatch}
        filterProps={filters}
        tableLoadingControls={{ isTableDataLoading, setIsTableDataLoading }}
      />
      {showScrollTop && <ScrollToTopButton />}
    </Container>
  );
}
